import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import IconTile from '../../components/common/IconTile';
import { getValues, ValueItem } from '../../components/home/ValuesTrio';
import { LIGHT_GRAY, PRIMARY_COLORS, WHITE } from '../../common/colors';
import logo1 from '../../assets/leadership/image-5_1.avif';
import logo2 from '../../assets/leadership/image-3.avif';
import logo3 from '../../assets/leadership/image-4.avif';
import logo4 from '../../assets/leadership/image-6.avif';
import logo5 from '../../assets/leadership/image-7.avif';
import logo6 from '../../assets/ciel-logo-colored.svg';
import PageMeta from '../../components/common/PageMeta';

// About us: hero + community photo, our-community + stakeholders,
// History/Vision/Mission foundations, community values trio, Leadership
// Committee. Each section's eyebrow/heading pair belongs to the content
// beneath it — they had drifted one section apart (ocl_online#141).
const STAKEHOLDER_ICONS = [GroupsOutlinedIcon, LocalHospitalOutlinedIcon];

const getStakeholders = t =>
  t('about.stakeholders', { returnObjects: true }).map((item, i) => ({ ...item, icon: STAKEHOLDER_ICONS[i] }));

const LEADERSHIP_LOGOS = [logo1, logo2, logo3, logo4, logo5, logo6];
// Organization names are proper nouns — identical across locales, so not
// run through i18n (unlike leadership_logo_alt, which supplies the
// translated "member organization" suffix for context).
const LEADERSHIP_ORG_NAMES = ['GlobalConnect', 'Regenstrief Institute', 'Apelon', 'OpenMRS', 'HL7 International', 'CIEL'];
// Held until every member organization has agreed to be listed. Flip to true
// to publish the grid — the logos and names above are kept ready for that.
const SHOW_LEADERSHIP_LOGOS = false;

// The community photo overlaps the bottom of the hero by this share of its
// own height: the band behind it is hero-blue down to that line. It is kept
// to 808px (70% of the lg container) so it reads as a community rather than a
// row of portraits (ocl_online#235). The file is 2x that width, 5:2.
const PHOTO_OVERLAP = '22%';

// Served from public/images rather than imported: prerender.js inlines
// imported images into the HTML as data: URIs, which suits logos, not a
// 230 KB photo.
const communityPhoto = '/images/about/community-photo.jpg';

const AboutUs = () => {
  const { t } = useTranslation();
  const foundations = t('about.foundations', { returnObjects: true });
  return (
    <Box>
      <PageMeta title={t('about.hero_title')} description={t('about.meta_description')} />
      <Box sx={{ backgroundColor: 'primary.main' }}>
        <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 9 }, pb: { xs: 4, md: 5 }, textAlign: 'center' }}>
          <Typography sx={{ color: PRIMARY_COLORS['90'], fontWeight: 600, mb: 1 }}>
            {t('about.hero_eyebrow')}
          </Typography>
          <Typography variant="h1" sx={{ color: WHITE, fontSize: { xs: '2.5rem', md: '3.25rem' }, mb: 2 }}>
            {t('about.hero_title')}
          </Typography>
          <Typography sx={{ color: PRIMARY_COLORS['90'], fontSize: '1.125rem', maxWidth: 820, mx: 'auto' }}>
            {t('about.hero_text')}
          </Typography>
        </Container>
      </Box>
      <Box sx={{ background: theme => `linear-gradient(to bottom, ${theme.palette.primary.main} ${PHOTO_OVERLAP}, transparent ${PHOTO_OVERLAP})` }}>
        <Container maxWidth="lg">
          <Box
            component="img"
            src={communityPhoto}
            alt={t('about.photo_alt')}
            width={1616}
            height={646}
            sx={{
              display: 'block',
              width: '100%',
              maxWidth: 808,
              height: 'auto',
              mx: 'auto',
              border: `1px solid ${LIGHT_GRAY}`,
              borderRadius: '12px',
              boxShadow: '0 1px 2px rgba(26,26,44,.04), 0 18px 40px -16px rgba(26,26,44,.28)',
            }}
          />
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 6, alignItems: 'center' }}>
          <Box>
            <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {t('about.community_eyebrow')}
            </Typography>
            <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.5rem' }, mb: 2 }}>
              {t('about.community_heading')}
            </Typography>
            <Typography sx={{ color: 'surface.contrastText' }}>
              {t('about.community_text')}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 3 }}>
            {getStakeholders(t).map(({ icon, title, text }) => (
              <Box key={title} sx={{ display: 'flex', gap: 2.5 }}>
                <IconTile icon={icon} sx={{ flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ color: 'surface.dark', fontWeight: 700, mb: 0.5 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
                    {text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
      <Box sx={{ backgroundColor: PRIMARY_COLORS['95'] }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 5, md: 7 } }}>
            <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {t('about.foundations_eyebrow')}
            </Typography>
            <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.5rem' } }}>
              {t('about.foundations_heading')}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 4, md: 6 } }}>
            {foundations.map(({ title, text }) => (
              <Box key={title}>
                <Typography variant="h5" component="h3" sx={{ color: 'surface.dark', mb: 1.5 }}>
                  {title}
                </Typography>
                <Typography sx={{ color: 'surface.contrastText' }}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 5, md: 7 } }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            {t('about.values_eyebrow')}
          </Typography>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.5rem' } }}>
            {t('about.values_heading')}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 5, md: 6 } }}>
          {getValues(t).map(value => <ValueItem key={value.title} {...value} />)}
        </Box>
      </Container>
      <Box sx={{ backgroundColor: PRIMARY_COLORS['95'] }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
            {t('about.leadership_eyebrow')}
          </Typography>
          <Typography variant="h3" component="h2" sx={{ color: 'surface.dark', fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 1.5 }}>
            {t('about.leadership_heading')}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText', maxWidth: 720, mx: 'auto', mb: SHOW_LEADERSHIP_LOGOS ? 5 : 0 }}>
            {t('about.leadership_text')}
          </Typography>
          {SHOW_LEADERSHIP_LOGOS && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 4, md: 7 } }}>
              {LEADERSHIP_LOGOS.map((logo, i) => (
                <Box key={i} component="img" src={logo} alt={`${LEADERSHIP_ORG_NAMES[i]} — ${t('about.leadership_logo_alt')}`} sx={{ maxHeight: 56, maxWidth: 150 }} />
              ))}
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default AboutUs;
